import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs';
import { appState } from '../interfaces/appState.interface';
import { ResponseRequest } from '@shared-logic/shared-models';
import { Order, OrderExchange } from '@shared-logic/orders';
import { Price } from '@shared-logic/prices';
import { DateChecker } from '@shared-logic/utils';
import { OrderService } from './order.service';
import { PriceService } from './price.service';
import { ExchangeService } from './exchange.service';
import { localStorageNames } from '../const/localStorageNames';
import {
  loadOrdersAllSuccess,
  loadOrdersAllWithPipesSuccess
} from '../../redux/actions/order.action';
import {
  loadOrdersAllSuccessInControlPanel
} from '../../redux/actions/control-panel.action';
import {
  loadPricesAllSuccess
} from '../../redux/actions/price.action';
import {
  loadExchangesAllSuccess
} from '../../redux/actions/exchange.action';
import {
  setLastUpdate,
  setLastUpdateHasError,
  setErrorStruct
} from '../../redux/actions/options.action';

@Injectable({
  providedIn: 'root'
})
export class DataRefreshService {

  constructor(
    private store: Store<appState>,
    private dateChecker: DateChecker,
    private orderService: OrderService,
    private priceService: PriceService,
    private exchangeService: ExchangeService
  ) { }

  private setLocalStorageSafely(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      const isQuotaExceeded = error instanceof DOMException && error.name === 'QuotaExceededError';
      if (isQuotaExceeded) {
        console.warn(`[localStorage] Quota exceeded while setting ${key}. Data will remain only in store.`);
        return;
      }
      throw error;
    }
  }

  /**
   * Refresh orders data via HTTP and update Redux store
   * @param updateControlPanel - Whether to also update control panel orders
   */
  refreshOrders(updateControlPanel: boolean = false): void {
    this.orderService.getOrdersToday().pipe(
      catchError((error) => {
        console.error('Error refreshing orders:', error.error);
        this.store.dispatch(loadOrdersAllSuccess({ orders: [] }));
        this.store.dispatch(loadOrdersAllWithPipesSuccess({ orders: [] }));

        if (updateControlPanel) {
          this.store.dispatch(loadOrdersAllSuccessInControlPanel({ orders: [] }));
        }

        this.store.dispatch(setLastUpdateHasError({ hasError: true }));
        this.store.dispatch(setErrorStruct({
          error: {
            color: '#fff',
            isVisible: true,
            message: 'No hay datos para mostrar'
          }
        }));
        return [];
      })
    ).subscribe((res: ResponseRequest) => {
      const orders: Order[] = !res.data ? [] : res.data;
      this.setLocalStorageSafely(localStorageNames.ordersAll, orders);

      this.store.dispatch(loadOrdersAllSuccess({ orders }));

      if (updateControlPanel) {
        this.store.dispatch(loadOrdersAllSuccessInControlPanel({ orders }));
      }

      this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
    });
  }

  /**
   * Refresh prices data via HTTP and update Redux store
   */
  refreshPrices(): void {
    const date = this.dateChecker.getDateInYYYYMMDDformat(
      this.dateChecker.stringToDate(this.dateChecker.getDate())
    );

    this.priceService.getPricesByDate(date).pipe(
      catchError((error) => {
        console.error('Error refreshing prices:', error?.error?.error?.REQUEST);
        this.store.dispatch(loadPricesAllSuccess({ prices: [] }));
        this.store.dispatch(setLastUpdateHasError({ hasError: true }));
        return [];
      })
    ).subscribe((response: ResponseRequest) => {
      const prices: Price[] = response.data ? response.data : [];
      this.setLocalStorageSafely(localStorageNames.pricesAll, prices);
      this.store.dispatch(loadPricesAllSuccess({ prices }));
      this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
    });
  }

  /**
   * Refresh exchanges data via HTTP and update Redux store
   */
  refreshExchanges(): void {
    this.exchangeService.getExchangesToday().pipe(
      catchError((error) => {
        console.error('Error refreshing exchanges:', error);
        this.store.dispatch(loadExchangesAllSuccess({ orders: [] }));
        this.store.dispatch(setLastUpdateHasError({ hasError: true }));
        return [];
      })
    ).subscribe((res: ResponseRequest) => {
      const exchanges = res?.data ? res.data : [];
      this.setExchanges(exchanges);
    });
  }

  /**
   * Helper method to set exchanges in store and localStorage
   */
  private setExchanges(exchanges: OrderExchange[]): void {
    const almacenamiento = !exchanges ? [] : exchanges;
    this.setLocalStorageSafely(localStorageNames.ordersExchange, almacenamiento);
    this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
    this.store.dispatch(loadExchangesAllSuccess({ orders: almacenamiento }));
  }
}